import { GuideSection } from '@app/components/Guide/ui/GuideSection'
import { fetchDemoBySlug } from '@app/services/strapiServices'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { DemoPageLoading } from './DemoPageLoading'
import { lazy } from 'react'
import { ShareButton } from '@app/components/Guide/ui/ShareButton'

const UiMarkdown = lazy(() => import('@app/components/Atoms/UiMarkdown/UiMarkdown'))

const toEmbedUrl = (url: string): string => {
  // YouTube
  const youtube = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/)
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}`

  // Vimeo
  const vimeo = url.match(/vimeo\.com\/(\d+)/)
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`

  return url
}

export const DemoPage = () => {
  const { articleSlug } = useParams()
  const {
    data: demo,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: ['demo-page', articleSlug],
    queryFn: () => fetchDemoBySlug(articleSlug as string),
    enabled: !!articleSlug,
  })

  if (isLoading) return <DemoPageLoading />
  if (error) return <DemoPageLoading isError={isError} errorMessage={"Couldn't load the demo"} />

  return (
    <main className="flex min-h-[1000px] w-full flex-col items-center bg-background">
      <GuideSection>
        <div className="relative h-[50vh] min-h-[360px] w-full overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <iframe
            src={toEmbedUrl(demo?.video?.url ?? '')}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={demo?.title}
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
      </GuideSection>

      <GuideSection className="flex-1">
        <div className="flex flex-col items-start w-full gap-4 p-6 border shadow-sm rounded-xl border-border bg-card text-card-foreground sm:p-8">
          <div className="flex flex-col items-start justify-between w-full gap-4 sm:flex-row sm:items-center">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {demo?.title}
            </h1>

            <ShareButton />
          </div>

          <div className="max-w-3xl text-sm leading-7 text-muted-foreground">
            <UiMarkdown>{demo?.summary ?? 'No summary'}</UiMarkdown>
          </div>
        </div>
      </GuideSection>
    </main>
  )
}
